import type { Ref } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { detectIfTruncated } from '../../common-functions';
import Input from '../../fields/input/input.component';
import Tooltip from '../../tooltip-component/tooltip.component';
import { CELL_PADDING_LEFT_PX } from '../data-grid-functions';
import type { DataGridDataCellBaseProps } from '../data-grid-settings.interface';

export type DataGridDataTextCellProps = DataGridDataCellBaseProps<string | number | bigint> & {
  width?: number;
  columnId?: string;
  rowId?: string;
  onMeasured?: (columnId: string, width: number, rowId?: string) => void;
  isSizingActive?: boolean;
};

function DataGridDataTextCell(
  { value, isEditing = false, width, columnId, rowId, onMeasured, isSizingActive }: DataGridDataTextCellProps,
  forwardRef: Ref<HTMLDivElement>
) {
  const cellRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useImperativeHandle(forwardRef, () => cellRef.current as HTMLDivElement);

  useEffect(() => {
    if (cellRef.current) {
      setIsTruncated(detectIfTruncated(cellRef.current));
    }
  }, [width]);

  const cellValue = String(value ?? '');
  // Measure content for auto-sizing column in datagrid after layout (only during first sizing session)
  useLayoutEffect(() => {
    if (!isSizingActive) return;
    if (!cellRef.current || !columnId || !onMeasured) return;

    const contentWidth = cellRef.current.scrollWidth + CELL_PADDING_LEFT_PX;
    if (contentWidth > 0) {
      onMeasured(columnId, contentWidth, rowId);
    }
  }, [isSizingActive, columnId, onMeasured, rowId, cellValue]);

  return isEditing ? (
    <Input type="text" name={cellValue} value={cellValue} trimWhitespace={false} />
  ) : (
    <Tooltip description={cellValue} tooltipClassName="bg-mono-1" disabled={!isTruncated} tooltipZIndexCheck={true}>
      {/*  NOTE: if modifying the padding 'pl-2', please remember to update all other cells as well as 'CELL_PADDING_LEFT_PX' */}
      <div className="pl-2 truncate" data-testid="data-grid-cell-content" ref={cellRef}>
        {cellValue}
      </div>
    </Tooltip>
  );
}

export default forwardRef(DataGridDataTextCell);
